
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Globe, Loader2, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/hooks/useCategories";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

const EditPromptPage = () => {
  const { promptId } = useParams<{ promptId: string }>();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const { categories, isLoading: isLoadingCategories } = useCategories();

  const [prompt, setPrompt] = useState({
    title: "",
    description: "",
    content: "",
    category_id: "",
    status: "",
    source: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Access denied. You do not have permission to edit prompts.");
      navigate("/");
      return;
    }

    const fetchPrompt = async () => {
      if (!promptId) {
        setLoadError("No prompt ID provided");
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching prompt with ID:", promptId);
        const { data, error } = await supabase
          .from("prompts")
          .select(`
            id, 
            title, 
            description, 
            content,
            category_id,
            status,
            source
          `)
          .eq("id", promptId)
          .single();

        if (error) {
          console.error("Supabase error:", error);
          throw error;
        }
        
        if (!data) {
          console.error("No prompt found with ID:", promptId);
          throw new Error("Prompt not found");
        }

        console.log("Prompt data loaded:", data);
        setPrompt({
          title: data.title || "",
          description: data.description || "",
          content: data.content || "",
          category_id: data.category_id || "",
          status: data.status || "pending",
          source: data.source || ""
        });

      } catch (error: any) {
        console.error("Error fetching prompt:", error);
        setLoadError(error.message || "Failed to load prompt");
        toast.error(`Error loading prompt: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompt();
  }, [promptId, isAdmin, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPrompt((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setPrompt((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promptId) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("prompts")
        .update({
          title: prompt.title,
          description: prompt.description,
          content: prompt.content,
          category_id: prompt.category_id,
          status: prompt.status,
          source: prompt.source || null
        })
        .eq("id", promptId);

      if (error) throw error;

      toast.success("Prompt updated successfully");
      navigate("/admin");
    } catch (error: any) {
      console.error("Error updating prompt:", error);
      toast.error(`Error updating prompt: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || isLoadingCategories) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading prompt data...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center">
          <div className="text-center max-w-lg">
            <h2 className="text-2xl font-bold text-destructive mb-4">Error Loading Prompt</h2>
            <p className="mb-6 text-muted-foreground">{loadError}</p>
            <Button onClick={() => navigate("/admin")}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Return to Admin
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8 flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              className="rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold">Edit Prompt</h1>
          </div>

          <Card className="border border-border/40 shadow-sm">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      name="title"
                      value={prompt.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={prompt.description}
                      onChange={handleChange}
                      required
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content">Prompt Content</Label>
                    <Textarea
                      id="content"
                      name="content"
                      value={prompt.content}
                      onChange={handleChange}
                      required
                      rows={10}
                      className="font-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={prompt.category_id}
                      onValueChange={(value) => handleSelectChange("category_id", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="source" className="flex items-center gap-2">
                      <span>Source</span>
                      <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="source"
                        name="source"
                        value={prompt.source || ""}
                        onChange={handleChange}
                        placeholder="e.g. reddit.com/r/chatgpt, twitter.com/username"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add attribution if this prompt is from another source like Reddit, X/Twitter, or other platforms
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={prompt.status}
                      onValueChange={(value) => handleSelectChange("status", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EditPromptPage;
